/**
 * PartnerCard — author/partner attribution block for a blog post.
 *
 * Extracted from PostArticle so that module stays within the file-size and
 * cyclomatic-complexity limits. Renders a static card in preview (or when the
 * partner has no slug) and a clickable link to the partner page otherwise.
 */
import { Link } from '@/shared/i18n/routing';
import { routes } from '@letsgethome/routes';
import {
  plainText,
  plaintextString,
  slug,
  type LocalizedText,
  type PlaintextString,
  type S3Key,
} from '@letsgethome/types/branded';
import { S3Image } from '@letsgethome/ui/s3-image';

export interface PartnerCardProps {
  partnerName: string;
  partnerSlug: string;
  partnerLogoKey: S3Key;
  /** Resolved, render-ready role label (already localized or plaintext). */
  partnerRole: LocalizedText | PlaintextString;
  isPreview: boolean;
}

export function PartnerCard({
  partnerName,
  partnerSlug,
  partnerLogoKey,
  partnerRole,
  isPreview,
}: PartnerCardProps) {
  const info = (
    <>
      <PartnerAvatar name={plainText(partnerName)} logoKey={partnerLogoKey || undefined} />
      <div>
        <p className="font-medium text-foreground">{plaintextString(partnerName)}</p>
        <p className="text-sm text-muted-foreground">{partnerRole}</p>
      </div>
    </>
  );

  return (
    <div className="mt-12 pt-8 border-t border-border">
      {isPreview || !partnerSlug ? (
        <div className="flex items-center gap-4 p-4 rounded-xl bg-muted">{info}</div>
      ) : (
        <Link
          href={routes.partners.detail(slug(partnerSlug))}
          className="flex items-center gap-4 p-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
        >
          {info}
        </Link>
      )}
    </div>
  );
}

function PartnerAvatar({ name, logoKey }: { name: LocalizedText; logoKey?: S3Key }) {
  if (logoKey) {
    return (
      <S3Image
        src={logoKey}
        alt={name}
        width={48}
        height={48}
        className="w-12 h-12 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
      {plaintextString(name.charAt(0))}
    </div>
  );
}
